import React from 'react';
import {
  Badge, Box, Heading, SimpleGrid, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiFileText, FiGrid } from 'react-icons/fi';

// RNWY 2.0 · P0 — the Add-Proposal FORK. item 647 (Chad, 2026-07-13): the chooser is
// **Standard | Automated**. "Automated" = the Schedule-B guided builder (answer-once venues → the
// engine prices it). dart (2026-07-15, Chad): the standalone Crestron/non-RNWY proposal path was
// scrapped as too convoluted — instead an "Export standard proposal" button on an RNWY proposal
// punts it into a real, editable Standard proposal (translated SKUs + scope). So the chooser is
// back to two cards. Rendered when the proposal is new and no ?kind= is set.

export type ProposalKind = 'standard' | 'crestron' | 'rnwy';

const OPTIONS: {
  kind: ProposalKind; title: string; blurb: string; icon: React.ReactNode; badge?: string;
}[] = [
  {
    kind: 'standard',
    title: 'Standard',
    blurb: 'The classic quote — build it from the item catalog with your general terms. No help desk.',
    icon: <FiFileText />,
  },
  {
    // "Automated" is the RNWY Sch-B guided builder (kind=1) — the three RNWY licensing options.
    kind: 'rnwy',
    title: 'Automated',
    blurb: 'The guided Schedule-B builder — answer each venue once and the engine prices the whole system automatically, with Help Desk options.',
    icon: <FiGrid />,
  },
];

const NewProposalChoice: React.FC<{ onPick: (kind: ProposalKind) => void }> = ({ onPick }) => {
  const cardBg = useColorModeValue('white', 'whiteAlpha.100');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const hover = useColorModeValue('brandGreen.400', 'brandGreen.300');

  return (
    <Box maxW="4xl" mx="auto" py={{ base: 6, md: 10 }} px={4}>
      <Heading size="lg" mb={1}>New proposal</Heading>
      <Text color={sub} mb={6}>Pick a template to start — it sets the whole flow.</Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {OPTIONS.map((o) => (
          <Box
            key={o.kind}
            role="button"
            tabIndex={0}
            onClick={() => onPick(o.kind)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPick(o.kind); }}
            borderWidth="1px"
            borderColor={cardBorder}
            bg={cardBg}
            borderRadius="lg"
            p={5}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ borderColor: hover, transform: 'translateY(-2px)', shadow: 'md' }}
          >
            <Box fontSize="28px" color={hover} mb={3}>{o.icon}</Box>
            <Heading size="md" mb={1}>
              {o.title}
              {o.badge && <Badge ml={2} colorScheme="purple" fontSize="0.6em" verticalAlign="middle">{o.badge}</Badge>}
            </Heading>
            <Text fontSize="sm" color={sub}>{o.blurb}</Text>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default NewProposalChoice;
